import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [heroData, setHeroData] = useState(null);
  const [aboutData, setAboutData] = useState(null);
  const [projectsData, setProjectsData] = useState([]);
  const [contactData, setContactData] = useState(null);
  const [messagesData, setMessagesData] = useState([]);

  // FORCE clear old localStorage on mount
  useEffect(() => {
    const portfolioData = localStorage.getItem('portfolioData');
    if (portfolioData) {
      try {
        const parsed = JSON.parse(portfolioData);
        // Check if it's old format (string instead of object)
        if (parsed.heroData && typeof parsed.heroData.title === 'string') {
          console.warn('🔄 Clearing old localStorage format...');
          localStorage.clear();
        }
      } catch (e) {
        console.warn('🔄 Clearing corrupted localStorage...');
        localStorage.clear();
      }
    }
  }, []);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      setApiError(null);
      const [heroRes, aboutRes, projectsRes, contactRes] = await Promise.all([
        fetch(`${API_URL}/hero`),
        fetch(`${API_URL}/about`),
        fetch(`${API_URL}/projects?status=active`),
        fetch(`${API_URL}/contact`)
      ]);

      const [hero, about, projects, contact] = await Promise.all([
        heroRes.json(),
        aboutRes.json(),
        projectsRes.json(),
        contactRes.json()
      ]);

      setHeroData(hero);
      setAboutData(about);
      setProjectsData(projects);
      setContactData(contact);
    } catch (error) {
      console.error('Error fetching data:', error);
      setApiError('Could not connect to the server. Showing cached data.');
      // Fallback to localStorage if API fails
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const loadFromLocalStorage = () => {
    const savedData = localStorage.getItem('portfolioData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Validate data structure - if it's old format, reset
        if (parsed.heroData && typeof parsed.heroData.title === 'string') {
          console.log('Old data format detected, resetting to defaults');
          localStorage.removeItem('portfolioData');
          setDefaults();
        } else {
          setHeroData(parsed.heroData || getDefaultHeroData());
          setAboutData(parsed.aboutData || getDefaultAboutData());
          setProjectsData(parsed.projectsData || []);
          setContactData(parsed.contactData || getDefaultContactData());
          setMessagesData(parsed.messagesData || []);
        }
      } catch (error) {
        console.error('Error parsing localStorage, resetting', error);
        localStorage.removeItem('portfolioData');
        setDefaults();
      }
    } else {
      setDefaults();
    }
  };

  const setDefaults = () => {
    setHeroData(getDefaultHeroData());
    setAboutData(getDefaultAboutData());
    setContactData(getDefaultContactData());
    setProjectsData([]);
  };

  const getDefaultHeroData = () => ({
    title: { tr: 'Merhaba, Ben Salih', en: 'Hello, I\'m Salih', de: 'Hallo, ich bin Salih' },
    subtitle: { tr: 'Yapay Zeka Mühendisi', en: 'AI Engineer', de: 'KI-Ingenieur' },
    description: { tr: 'Yapay zeka ve makine öğrenmesi alanında uzmanlaşmış bir yazılım mühendisiyim.', en: 'I am a software engineer specialized in AI and ML.', de: 'Ich bin ein Softwareingenieur.' },
    location: 'Istanbul, Turkey'
  });

  const getDefaultAboutData = () => ({
    title: { tr: 'Hakkımda', en: 'About Me', de: 'Über mich' },
    description: { tr: 'Yapay zeka mühendisiyim.', en: 'I am an AI engineer.', de: 'Ich bin KI-Ingenieur.' },
    skills: [],
    experience: 3,
    projectsCount: 15
  });

  const getDefaultContactData = () => ({
    email: 'contact@example.com',
    location: 'Istanbul, Turkey',
    social: { github: '', linkedin: '', twitter: '' },
    availability: { tr: 'Yeni projelere açığım', en: 'Available for new projects', de: 'Verfügbar' }
  });

  // Send message (contact form)
  const sendMessage = async (messageData) => {
    try {
      const res = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData)
      });

      if (!res.ok) throw new Error('Failed to send message');

      return { success: true };
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback to localStorage
      const newMessage = { ...messageData, id: Date.now(), createdAt: new Date().toISOString(), read: false };
      setMessagesData(prev => [...prev, newMessage]);
      return { success: true, offline: true };
    }
  };

  // Helper function to get localized content
  const getLocalizedContent = (data, field, lang = 'en') => {
    if (!data || !data[field]) return '';
    const value = data[field];
    // If it's already a string, return it
    if (typeof value === 'string') return value;
    // If it's an object with language keys
    if (typeof value === 'object' && value !== null) {
      return value[lang] || value['en'] || value['tr'] || '';
    }
    return '';
  };

  // Hero CRUD
  const heroCRUD = {
    data: heroData,
    getLocalized: (field, lang) => getLocalizedContent(heroData, field, lang),
    update: async (id, updatedData) => {
      setHeroData(prev => ({ ...prev, ...updatedData }));
    }
  };

  // About CRUD
  const aboutCRUD = {
    data: aboutData,
    getLocalized: (field, lang) => getLocalizedContent(aboutData, field, lang),
    update: async (id, updatedData) => {
      setAboutData(prev => ({ ...prev, ...updatedData }));
    },
    addSkill: (skill) => {
      setAboutData(prev => ({
        ...prev,
        skills: [...(prev.skills || []), skill]
      }));
    },
    removeSkill: (skill) => {
      setAboutData(prev => ({
        ...prev,
        skills: (prev.skills || []).filter(s => s !== skill && s.name !== skill)
      }));
    }
  };

  // Projects CRUD
  const projectsCRUD = {
    data: projectsData,
    getAll: () => projectsData,
    getFeatured: () => projectsData.filter(project => project.featured),
    getLocalized: (project, field, lang) => {
      if (!project || !project[field]) return '';
      if (typeof project[field] === 'string') return project[field];
      return project[field][lang] || project[field]['en'] || '';
    },
    create: async (newProject) => {
      const id = Date.now();
      setProjectsData(prev => [...prev, { ...newProject, id }]);
      return id;
    },
    update: async (id, updatedData) => {
      setProjectsData(prev =>
        prev.map(project => project.id === id || project._id === id ? { ...project, ...updatedData } : project)
      );
    },
    delete: async (id) => {
      setProjectsData(prev => prev.filter(project => project.id !== id && project._id !== id));
    },
    toggleFeatured: (id) => {
      setProjectsData(prev =>
        prev.map(project =>
          (project.id === id || project._id === id) ? { ...project, featured: !project.featured } : project
        )
      );
    }
  };

  // Contact CRUD
  const contactCRUD = {
    data: contactData,
    getLocalized: (field, lang) => getLocalizedContent(contactData, field, lang),
    update: async (id, updatedData) => {
      setContactData(prev => ({ ...prev, ...updatedData }));
    },
    updateSocial: (platform, url) => {
      setContactData(prev => ({
        ...prev,
        social: { ...prev.social, [platform]: url }
      }));
    }
  };

  // Messages CRUD
  const messagesCRUD = {
    data: messagesData,
    getAll: () => messagesData,
    create: sendMessage,
    markAsRead: (id) => {
      setMessagesData(prev =>
        prev.map(message =>
          message.id === id ? { ...message, read: true } : message
        )
      );
    },
    getUnread: () => messagesData.filter(message => !message.read),
    delete: (id) => {
      setMessagesData(prev => prev.filter(message => message.id !== id));
    }
  };

  // Persist to localStorage as backup
  useEffect(() => {
    if (!loading && heroData && aboutData && contactData) {
      const dataToSave = {
        heroData,
        aboutData,
        projectsData,
        contactData,
        messagesData
      };
      localStorage.setItem('portfolioData', JSON.stringify(dataToSave));
    }
  }, [heroData, aboutData, projectsData, contactData, messagesData, loading]);

  const value = {
    loading,
    apiError,
    dismissError: () => setApiError(null),
    hero: heroCRUD,
    about: aboutCRUD,
    projects: projectsCRUD,
    contact: contactCRUD,
    messages: messagesCRUD,
    sendMessage,
    refetch: fetchAllData,
    exportData: () => ({
      heroData,
      aboutData,
      projectsData,
      contactData,
      messagesData
    }),
    importData: (data) => {
      if (data.heroData) setHeroData(data.heroData);
      if (data.aboutData) setAboutData(data.aboutData);
      if (data.projectsData) setProjectsData(data.projectsData);
      if (data.contactData) setContactData(data.contactData);
      if (data.messagesData) setMessagesData(data.messagesData);
    },
    resetData: () => {
      localStorage.removeItem('portfolioData');
      window.location.reload();
    }
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
